import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useSelectedCourse } from '../../contexts/SelectedCourseContext';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
  cupoMaximo: number;
  cupoOcupado: number;
  activo: boolean;
  fechaAperturaInscripcion?: string;
  fechaCierreInscripcion?: string;
  inscripcionesHabilitadas: boolean;
  inscripcionesAbiertas?: boolean;
  estadoInscripciones?: string;
  curso: {
    id: number;
    nombre: string;
    codigo: string;
    anio?: number;
  };
  cursoId: number;
  createdAt: string;
  updatedAt: string;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  activo: boolean;
}

const TurnosPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingTurno, setEditingTurno] = useState<Turno | null>(null);
  const [showDetails, setShowDetails] = useState<Turno | null>(null);
  const [showInscripcionesModal, setShowInscripcionesModal] = useState<Turno | null>(null);
  const [fechasConfig, setFechasConfig] = useState({
    fechaApertura: '',
    fechaCierre: ''
  });
  const queryClient = useQueryClient();
  const { selectedCourse, courses } = useSelectedCourse();
  const [selectedCursoId, setSelectedCursoId] = useState<number | null>(
    selectedCourse?.id ?? null
  );

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    dia: 'LUNES',
    horaInicio: '',
    horaFin: '',
    cupoMaximo: 30,
    activo: true,
    cursoId: selectedCourse?.id?.toString() || ''
  });

  // Fetch turnos (filtrados por curso local si hay uno seleccionado)
  const { data: turnos, isLoading, error } = useQuery({
    queryKey: ['turnos', selectedCursoId],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/turnos`);
      let data = response.data;
      
      if (selectedCursoId) {
        data = data.filter((turno: Turno) => turno.cursoId === selectedCursoId);
      }
      
      return data;
    }
  });

  // Usar cursos del contexto
  const cursos = courses;

  // Create turno mutation
  const createTurnoMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/turnos`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
      setShowModal(false);
      resetForm();
    }
  });

  // Update turno mutation
  const updateTurnoMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/turnos/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
      setShowModal(false);
      setEditingTurno(null);
      resetForm();
    }
  });

  // Delete turno mutation
  const deleteTurnoMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`${process.env.REACT_APP_API_URL}/turnos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
    }
  });

  // Habilitar inscripciones mutation
  const habilitarInscripcionesMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/turnos/${id}/habilitar-inscripciones`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
    }
  });

  // Deshabilitar inscripciones mutation
  const deshabilitarInscripcionesMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/turnos/${id}/deshabilitar-inscripciones`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
    }
  });

  // Configurar fechas mutation
  const configurarFechasMutation = useMutation({
    mutationFn: async ({ id, fechaApertura, fechaCierre }: { id: number; fechaApertura?: string; fechaCierre?: string }) => {
      console.log('🔧 Frontend enviando fechas:', { id, fechaApertura, fechaCierre });
      
      // Asegurar que las fechas se envíen en formato local sin conversión de zona horaria
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/turnos/${id}/configurar-fechas`, {
        fechaApertura,
        fechaCierre
      });
      console.log('✅ Respuesta del backend:', response.data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
    }
  });

  const resetForm = () => {
    setFormData({
      nombre: '',
      dia: 'LUNES',
      horaInicio: '',
      horaFin: '',
      cupoMaximo: 30,
      activo: true,
      cursoId: selectedCursoId?.toString() || ''
    });
  };

  // Sincronizar selector local cuando el curso global del header cambia
  React.useEffect(() => {
    if (selectedCourse) {
      setSelectedCursoId(selectedCourse.id);
    }
  }, [selectedCourse]);

  // Actualizar cursoId del formulario cuando cambia el curso seleccionado localmente
  React.useEffect(() => {
    if (selectedCursoId && !editingTurno) {
      setFormData(prev => ({
        ...prev,
        cursoId: selectedCursoId.toString()
      }));
    }
  }, [selectedCursoId, editingTurno]);

  const handleEdit = (turno: Turno) => {
    setEditingTurno(turno);
    setFormData({
      nombre: turno.nombre,
      dia: turno.dia,
      horaInicio: turno.horaInicio,
      horaFin: turno.horaFin,
      cupoMaximo: turno.cupoMaximo,
      activo: turno.activo,
      cursoId: turno.cursoId.toString()
    });
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      cursoId: parseInt(formData.cursoId)
    };

    if (editingTurno) {
      updateTurnoMutation.mutate({ id: editingTurno.id, data: submitData });
    } else {
      createTurnoMutation.mutate(submitData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este turno?')) {
      deleteTurnoMutation.mutate(id);
    }
  };

  const getDiaLabel = (dia: string) => {
    const dias = {
      'LUNES': 'Lunes',
      'MARTES': 'Martes',
      'MIERCOLES': 'Miércoles',
      'JUEVES': 'Jueves',
      'VIERNES': 'Viernes',
      'SABADO': 'Sábado',
      'DOMINGO': 'Domingo'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  // Función helper para formatear fechas en zona horaria local
  const formatDateLocal = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      // Mostrar en zona horaria local sin conversión
      return date.toLocaleString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Argentina/Buenos_Aires'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return dateString;
    }
  };

  const getCupoPercentage = (ocupado: number, maximo: number) => {
    return Math.round((ocupado / maximo) * 100);
  };

  if (isLoading) return <div className="text-center py-8">Cargando turnos...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error al cargar los turnos</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Turnos</h2>
          <select
            value={selectedCursoId ?? ''}
            onChange={(e) => setSelectedCursoId(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los cursos</option>
            {Array.isArray(courses) && courses.map((c: Curso) => (
              <option key={c.id} value={c.id}>{formatCursoLabel(c)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nuevo Turno
        </button>
      </div>

      {/* Lista de turnos */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Turnos ({turnos?.length || 0})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Turno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Curso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cupo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscripciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(turnos) && turnos.map((turno: Turno) => (
                <tr key={turno.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{turno.nombre}</div>
                      <div className="text-sm text-gray-500">{getDiaLabel(turno.dia)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatCursoNombreAnio(turno.curso)}</div>
                      <div className="text-sm text-gray-500">{turno.curso?.codigo}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {turno.horaInicio} - {turno.horaFin}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div className="text-sm text-gray-900">
                          {turno.cupoOcupado}/{turno.cupoMaximo}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo) > 80 
                                ? 'bg-red-500' 
                                : getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo) > 60 
                                ? 'bg-yellow-500' 
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${getCupoPercentage(turno.cupoOcupado, turno.cupoMaximo)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      turno.activo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {turno.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        turno.inscripcionesAbiertas 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {turno.inscripcionesAbiertas ? 'Abiertas' : 'Cerradas'}
                      </span>
                      {turno.estadoInscripciones && (
                        <div className="text-xs text-gray-500">
                          {turno.estadoInscripciones}
                        </div>
                      )}
                      {turno.fechaAperturaInscripcion && (
                        <div className="text-xs text-blue-600">
                          Abre: {formatDateLocal(turno.fechaAperturaInscripcion)}
                        </div>
                      )}
                      {turno.fechaCierreInscripcion && (
                        <div className="text-xs text-orange-600">
                          Cierra: {formatDateLocal(turno.fechaCierreInscripcion)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowDetails(turno)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setShowInscripcionesModal(turno);
                          // Configurar las fechas actuales en el estado
                          setFechasConfig({
                            fechaApertura: turno.fechaAperturaInscripcion ? 
                              new Date(turno.fechaAperturaInscripcion).toISOString().slice(0, 16) : '',
                            fechaCierre: turno.fechaCierreInscripcion ? 
                              new Date(turno.fechaCierreInscripcion).toISOString().slice(0, 16) : ''
                          });
                        }}
                        className="text-green-600 hover:text-green-900"
                        title="Configurar inscripciones"
                      >
                        📝
                      </button>
                      <button
                        onClick={() => handleEdit(turno)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(turno.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear/editar turno */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTurno ? 'Editar Turno' : 'Nuevo Turno'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre del Turno</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Ej: Turno Mañana"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Curso</label>
                    <select
                      value={formData.cursoId}
                      onChange={(e) => setFormData({...formData, cursoId: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Seleccionar curso</option>
                      {Array.isArray(cursos) && cursos.map((curso: Curso) => (
                        <option key={curso.id} value={curso.id}>
                          {formatCursoLabel(curso)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Día</label>
                    <select
                      value={formData.dia}
                      onChange={(e) => setFormData({...formData, dia: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="LUNES">Lunes</option>
                      <option value="MARTES">Martes</option>
                      <option value="MIERCOLES">Miércoles</option>
                      <option value="JUEVES">Jueves</option>
                      <option value="VIERNES">Viernes</option>
                      <option value="SABADO">Sábado</option>
                      <option value="DOMINGO">Domingo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hora de Inicio</label>
                    <input
                      type="time"
                      value={formData.horaInicio}
                      onChange={(e) => setFormData({...formData, horaInicio: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hora de Fin</label>
                    <input
                      type="time"
                      value={formData.horaFin}
                      onChange={(e) => setFormData({...formData, horaFin: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cupo Máximo</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.cupoMaximo}
                      onChange={(e) => setFormData({...formData, cupoMaximo: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Turno Activo</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingTurno(null);
                      resetForm();
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createTurnoMutation.isPending || updateTurnoMutation.isPending}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createTurnoMutation.isPending || updateTurnoMutation.isPending ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para detalles del turno */}
      {showDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles del Turno</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{showDetails.nombre}</h4>
                  <p className="text-sm text-gray-600">{formatCursoLabel(showDetails.curso)}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Día: {getDiaLabel(showDetails.dia)}</p>
                    <p className="text-sm font-medium text-gray-900">
                      Horario: {showDetails.horaInicio} - {showDetails.horaFin}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Cupo: {showDetails.cupoOcupado}/{showDetails.cupoMaximo}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      Estado: {showDetails.activo ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Ocupación del cupo</span>
                    <span className="text-sm text-gray-500">
                      {getCupoPercentage(showDetails.cupoOcupado, showDetails.cupoMaximo)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        getCupoPercentage(showDetails.cupoOcupado, showDetails.cupoMaximo) > 80 
                          ? 'bg-red-500' 
                          : getCupoPercentage(showDetails.cupoOcupado, showDetails.cupoMaximo) > 60 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${getCupoPercentage(showDetails.cupoOcupado, showDetails.cupoMaximo)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowDetails(null)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para configurar inscripciones */}
      {showInscripcionesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Configurar Inscripciones - {showInscripcionesModal.nombre}
              </h3>
              
              <div className="space-y-6">
                {/* Estado actual */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="font-medium text-gray-900 mb-2">Estado Actual</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Estado General:</span>
                      <span className={`text-sm font-medium ${
                        showInscripcionesModal.inscripcionesAbiertas ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {showInscripcionesModal.inscripcionesAbiertas ? 'ABIERTAS' : 'CERRADAS'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Flag Manual:</span>
                      <span className={`text-sm font-medium ${
                        showInscripcionesModal.inscripcionesHabilitadas ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {showInscripcionesModal.inscripcionesHabilitadas ? 'Habilitado' : 'Deshabilitado'}
                      </span>
                    </div>
                    {showInscripcionesModal.estadoInscripciones && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Detalle:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {showInscripcionesModal.estadoInscripciones}
                        </span>
                      </div>
                    )}
                    {showInscripcionesModal.fechaAperturaInscripcion && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fecha Apertura:</span>
                        <span className="text-sm font-medium text-blue-600">
                          {formatDateLocal(showInscripcionesModal.fechaAperturaInscripcion)}
                        </span>
                      </div>
                    )}
                    {showInscripcionesModal.fechaCierreInscripcion && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fecha Cierre:</span>
                        <span className="text-sm font-medium text-orange-600">
                          {formatDateLocal(showInscripcionesModal.fechaCierreInscripcion)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Controles rápidos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Controles Rápidos</h4>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        habilitarInscripcionesMutation.mutate(showInscripcionesModal.id);
                        setShowInscripcionesModal(null);
                      }}
                      disabled={habilitarInscripcionesMutation.isPending}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      Habilitar Inscripciones
                    </button>
                    <button
                      onClick={() => {
                        deshabilitarInscripcionesMutation.mutate(showInscripcionesModal.id);
                        setShowInscripcionesModal(null);
                      }}
                      disabled={deshabilitarInscripcionesMutation.isPending}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      Deshabilitar Inscripciones
                    </button>
                  </div>
                </div>

                {/* Configuración de fechas */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Configurar Fechas Automáticas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Apertura
                      </label>
                      <input
                        type="datetime-local"
                        value={fechasConfig.fechaApertura}
                        onChange={(e) => setFechasConfig({...fechasConfig, fechaApertura: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Cierre
                      </label>
                      <input
                        type="datetime-local"
                        value={fechasConfig.fechaCierre}
                        onChange={(e) => setFechasConfig({...fechasConfig, fechaCierre: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        configurarFechasMutation.mutate({
                          id: showInscripcionesModal.id,
                          fechaApertura: fechasConfig.fechaApertura || undefined,
                          fechaCierre: fechasConfig.fechaCierre || undefined
                        });
                        setShowInscripcionesModal(null);
                      }}
                      disabled={configurarFechasMutation.isPending}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Configurar Fechas
                    </button>
                    <button
                      onClick={() => {
                        configurarFechasMutation.mutate({
                          id: showInscripcionesModal.id,
                          fechaApertura: undefined,
                          fechaCierre: undefined
                        });
                        setShowInscripcionesModal(null);
                      }}
                      disabled={configurarFechasMutation.isPending}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                    >
                      Limpiar Fechas
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowInscripcionesModal(null)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurnosPage;
